import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { logError } from '@/lib/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const useLineups = (query) => {
  const queryKey = JSON.stringify(query || {});
  const [data, setData] = useState(null);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [company, setCompany] = useState('');
  const [lineupBy, setLineupBy] = useState('');
  const [telecaller, setTelecaller] = useState('');

  const load = useCallback(async () => {
    try {
      const params = JSON.parse(queryKey);
      const [l, m] = await Promise.all([axios.get(`${API}/lineups`, { params }), axios.get(`${API}/lineups/meta`)]);
      setData(l.data);
      setMeta(m.data);
    } catch (e) {
      logError('Error loading lineups:', e);
      toast.error('Failed to load lineups');
    } finally { setLoading(false); }
  }, [queryKey]);

  useEffect(() => { load(); }, [load]);

  const rowsNoCompany = useMemo(() => (data?.rows || []).filter((r) =>
    (!lineupBy || r.lineup_by === lineupBy) && (!telecaller || r.telecaller === telecaller)
  ), [data, lineupBy, telecaller]);

  const rows = useMemo(() => rowsNoCompany.filter((r) => !company || r.company_name === company), [rowsNoCompany, company]);

  const options = useMemo(() => {
    const all = data?.rows || [];
    const uniq = (k) => [...new Set(all.map((r) => r[k]).filter(Boolean))].sort();
    return { companies: uniq('company_name'), lineupBys: uniq('lineup_by'), telecallers: uniq('telecaller') };
  }, [data]);

  const patchLocal = (updated) => setData((d) => d && ({ ...d, rows: d.rows.map((r) => (r.id === updated.id ? updated : r)) }));

  const updateStatus = async (row, status, reason) => {
    try {
      const { data: upd } = await axios.patch(`${API}/lineups/${row.id}`, { status, ...(reason ? { not_going_reason: reason } : {}) });
      patchLocal(upd);
      load();
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update status');
      return false;
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete ${row.candidate_name} (${row.mobile}) from today's lineup?`)) return;
    try {
      await axios.delete(`${API}/lineups/${row.id}`);
      toast.success('Lineup deleted');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to delete'); }
  };

  const sendToTracker = async (row) => {
    try {
      const { data: res } = await axios.post(`${API}/lineups/${row.id}/send-to-tracker`);
      toast.success(`${res.candidate_name} added to the Candidate Tracker`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to send to Tracker'); }
  };

  const add = async (form) => {
    try {
      await axios.post(`${API}/lineups`, form);
      toast.success(`${form.candidate_name} lined up for ${form.company_name}`);
      load();
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add lineup');
      return false;
    }
  };

  const clearFilters = () => { setCompany(''); setLineupBy(''); setTelecaller(''); };

  return {
    data, meta, loading, rows, rowsNoCompany, options, reload: load,
    company, setCompany, lineupBy, setLineupBy, telecaller, setTelecaller, clearFilters,
    updateStatus, remove, sendToTracker, add,
  };
};
